import PublicLayout from "@/components/PublicLayout";
import { Lightbulb, Target, Heart, Compass, ShieldCheck, Sparkles } from "lucide-react";
import { useSEO } from "@/lib/useSEO";

const VALUES = [
  { icon: Lightbulb, title: "Clarity over complexity", desc: "Insurance is too important to be obscured by jargon. We rewrite it in plain English." },
  { icon: Target, title: "Educational, not transactional", desc: "We never push policies. We help you understand them so you can decide." },
  { icon: Heart, title: "User-aligned by design", desc: "No insurer commissions. No affiliate quotas. The answer that's best for you is the only answer." },
  { icon: Compass, title: "Source-backed reasoning", desc: "Every claim traces back to public documents, brochures, or IRDAI guidelines." },
  { icon: ShieldCheck, title: "Privacy-first architecture", desc: "Your conversations belong to you. Profile data is encrypted at rest and never sold." },
  { icon: Sparkles, title: "Built on world-class AI", desc: "Powered by Claude Sonnet 4.5 — chosen for nuance, neutrality, and reasoning fidelity." },
];

export default function About() {
  useSEO({
    title: "About InsureGenie — AI Insurance Advisor for Indian Families",
    description: "Learn how InsureGenie helps Indian customers understand health, term life and tax-saving insurance policies with clear, IRDAI-aligned AI guidance in plain English.",
  });
  return (
    <PublicLayout
      eyebrow="About InsureGenie"
      title="Insurance, reimagined as a conversation."
    >
      <p className="lead text-lg text-muted-foreground !leading-relaxed">
        InsureGenie is an AI Insurance Intelligence Platform. We help people understand,
        compare, and analyze insurance policies through natural conversation — not endless
        comparison tables or aggressive sales scripts.
      </p>

      <h2>Why we built this</h2>
      <p>
        Most insurance buyers in India read fewer than 10% of their policy document before
        committing to it. The result: surprise exclusions, hospital sub-limits that bite at
        the worst time, and term plans that pay less than expected. We believe that&apos;s
        a clarity problem — not an awareness problem — and clarity is something AI can
        finally deliver at scale.
      </p>

      <h2>What InsureGenie is</h2>
      <ul>
        <li><strong>An AI-native explainer.</strong> Ask any insurance question — health, life, term, motor, travel — and get a clear, structured answer with sources.</li>
        <li><strong>A comparison engine.</strong> Get side-by-side breakdowns of plans across insurers, with the trade-offs called out plainly.</li>
        <li><strong>A document intelligence layer.</strong> Upload a brochure or policy PDF and InsureGenie reads, summarizes, and surfaces the fine print that matters.</li>
        <li><strong>A personalization layer.</strong> Once you share your profile (age, family, conditions, income goals), every answer is tailored to your context.</li>
      </ul>

      <h2>What InsureGenie is not</h2>
      <ul>
        <li>Not an insurance company, broker, or aggregator.</li>
        <li>Not a tax, legal, or financial advisor.</li>
        <li>Not a substitute for a conversation with a qualified professional — especially for life-altering decisions.</li>
      </ul>

      <h2>The principles we engineer to</h2>
      <div className="not-prose grid sm:grid-cols-2 gap-4 my-8">
        {VALUES.map((v) => (
          <div key={v.title} data-testid={`about-value-${v.title}`} className="bg-card border border-border rounded-2xl p-5">
            <div className="w-10 h-10 rounded-xl bg-accent/10 text-accent flex items-center justify-center mb-4">
              <v.icon className="w-5 h-5" />
            </div>
            <div className="font-display font-semibold text-base">{v.title}</div>
            <p className="text-sm text-muted-foreground mt-1.5 leading-relaxed">{v.desc}</p>
          </div>
        ))}
      </div>

      <h2>The road ahead</h2>
      <p>
        We&apos;re building toward a future where every insurance decision is a 60-second
        conversation, not a 60-page PDF. That means deeper document understanding,
        comparison across thousands of policies, multi-language support, and tight
        integrations with regulator-published data. We&apos;re early — and that&apos;s the
        best time to shape the product with us.
      </p>
    </PublicLayout>
  );
}
